import React, { memo, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Types
type KeywordEntry = {
  keywords: readonly string[];
  priority: number;
};

// Keyword map of contexts -> keywords and priority
export const keywordMap = {
  greeting: {
    keywords: [
      "hi",
      "hello",
      "hey",
      "good morning",
      "good evening",
      "good afternoon",
    ],
    priority: 1,
  },
  appreciation: {
    keywords: [
      "thanks",
      "thank you",
      "appreciate",
      "great",
      "awesome",
      "perfect",
      "cool",
    ],
    priority: 1,
  },
  engine: {
    keywords: [
      "engine",
      "oil",
      "fuel",
      "ignition",
      "spark plug",
      "misfire",
      "rpm",
      "stalling",
      "overheating",
      "idle",
    ],
    priority: 10,
  },
  brakes: {
    keywords: [
      "brake",
      "abs",
      "stopping",
      "squeak",
      "grinding",
      "brake pad",
      "brake fluid",
      "rotor",
    ],
    priority: 8,
  },
  ac: {
    keywords: [
      "ac",
      "air conditioning",
      "cooling",
      "heater",
      "climate control",
      "refrigerant",
      "compressor",
    ],
    priority: 7,
  },
  battery: {
    keywords: [
      "battery",
      "charging",
      "electrical",
      "alternator",
      "dead battery",
      "jump start",
      "voltage",
    ],
    priority: 9,
  },
  infotainment: {
    keywords: [
      "infotainment",
      "screen",
      "display",
      "touch screen",
      "music",
      "radio",
      "bluetooth",
      "sound system",
      "android auto",
      "carplay",
    ],
    priority: 4,
  },
  tires: {
    keywords: [
      "tire",
      "wheel",
      "pressure",
      "puncture",
      "flat",
      "alignment",
      "balancing",
    ],
    priority: 6,
  },
  transmission: {
    keywords: [
      "transmission",
      "gear",
      "gearbox",
      "shifting",
      "clutch",
      "automatic",
      "manual",
    ],
    priority: 8,
  },
  lights: {
    keywords: [
      "headlight",
      "tail light",
      "indicator",
      "blinker",
      "fog light",
      "bulb",
      "led",
    ],
    priority: 4,
  },
  warning: {
    keywords: [
      "check engine",
      "dashboard",
      "warning light",
      "indicator light",
      "service light",
    ],
    priority: 9,
  },
  doors: {
    keywords: [
      "door",
      "lock",
      "window",
      "power window",
      "central locking",
      "stuck door",
    ],
    priority: 3,
  },
  wipers: { keywords: ["wiper", "windshield", "washer", "fluid"], priority: 3 },
  exhaust: {
    keywords: ["exhaust", "emission", "smoke", "muffler", "catalytic"],
    priority: 5,
  },
  suspension: {
    keywords: [
      "suspension",
      "steering",
      "shock absorber",
      "alignment",
      "rack",
      "column",
    ],
    priority: 6,
  },
  fuel: {
    keywords: ["fuel pump", "injector", "fuel filter", "petrol", "diesel"],
    priority: 7,
  },
  dealer_contact: {
    keywords: [
      "dealer",
      "showroom",
      "contact",
      "sales rep",
      "salesperson",
      "call dealer",
      "nearest dealer",
      "book test drive",
    ],
    priority: 10,
  },
  service_scheduling: {
    keywords: [
      "schedule service",
      "book service",
      "maintenance",
      "appointment",
      "oil change",
      "tire rotation",
      "service booking",
      "service date",
    ],
    priority: 10,
  },
  offers: {
    keywords: [
      "offers",
      "deals",
      "discount",
      "promotion",
      "scheme",
      "incentive",
      "cashback",
    ],
    priority: 7,
  },
  finance: {
    keywords: [
      "emi",
      "finance",
      "loan",
      "downpayment",
      "warranty",
      "coverage",
      "interest rate",
      "financing options",
    ],
    priority: 7,
  },
} as const satisfies Record<string, KeywordEntry>;

export type ContextKey = keyof typeof keywordMap;

// Messages by context. Must include 'generic' plus all context keys.
export const thinkingMessages = {
  generic: [
    "Analyzing user intent...",
    "Finding details from knowledge base...",
    "Querying system knowledge graph...",
    "Optimizing context resolution engine...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  greeting: [
    "Establishing secure connection...",
    "Finding details about greeting from knowledge base...",
    "Syncing with system modules...",
    "Fetching initial resources...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  appreciation: [
    "Acknowledging appreciation...",
    "Finding details about appreciation from knowledge base...",
    "Updating session context...",
    "Optimizing data stream for next step...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  engine: [
    "Querying engine diagnostics module...",
    "Finding details about engine from knowledge base...",
    "Compiling lubrication and fuel system data...",
    "Analyzing combustion cycle efficiency...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  brakes: [
    "Engaging brake safety protocol checks...",
    "Finding details about brakes from knowledge base...",
    "Loading ABS performance logs...",
    "Scanning rotor and pad wear metrics...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  ac: [
    "Activating climate control diagnostic layer...",
    "Finding details about AC system from knowledge base...",
    "Analyzing refrigerant pressure patterns...",
    "Cross-verifying compressor torque consistency...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  battery: [
    "Initiating voltage stability analysis...",
    "Finding details about battery from knowledge base...",
    "Tracing alternator feedback signals...",
    "Evaluating charge retention profiles...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  infotainment: [
    "Loading infotainment system topology...",
    "Finding details about infotainment from knowledge base...",
    "Resolving connectivity stack dependencies...",
    "Validating audio-visual subsystem integrity...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  tires: [
    "Fetching TPMS data streams...",
    "Finding details about tires from knowledge base...",
    "Analyzing tread wear patterns...",
    "Aligning balance metrics...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  transmission: [
    "Scanning gearbox actuator states...",
    "Finding details about transmission from knowledge base...",
    "Mapping torque converter patterns...",
    "Running clutch slip analysis...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  lights: [
    "Inspecting illumination control matrix...",
    "Finding details about lights from knowledge base...",
    "Validating signal path integrity...",
    "Loading headlamp configuration nodes...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  warning: [
    "Parsing DTC codes from ECU memory...",
    "Finding details about warnings from knowledge base...",
    "Correlating fault code hierarchy...",
    "Running advanced alert prioritization algorithm...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  doors: [
    "Checking actuator response curve...",
    "Finding details about doors from knowledge base...",
    "Reading door module CAN signals...",
    "Simulating lock/unlock sequences...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  wipers: [
    "Polling rain-sensor triggers...",
    "Finding details about wipers from knowledge base...",
    "Inspecting wiper relay continuity...",
    "Mapping wash fluid flow metrics...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  exhaust: [
    "Running catalytic efficiency scans...",
    "Finding details about exhaust from knowledge base...",
    "Checking O2 sensor oscillations...",
    "Measuring backpressure deviations...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  suspension: [
    "Stabilizing steering rack data stream...",
    "Finding details about suspension from knowledge base...",
    "Measuring suspension deflection trends...",
    "Reviewing damping coefficients...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  fuel: [
    "Evaluating injector pulse timings...",
    "Finding details about fuel system from knowledge base...",
    "Measuring fuel rail pressure waveforms...",
    "Confirming filter delta-pressure thresholds...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  dealer_contact: [
    "Querying dealer network index...",
    "Finding dealer contact details from knowledge base...",
    "Resolving sales representative directory...",
    "Decrypting location-based contact cache...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  service_scheduling: [
    "Activating service workflow engine...",
    "Finding details about service schedule from knowledge base...",
    "Fetching available appointment slots...",
    "Cross-referencing dealer capacity metrics...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  offers: [
    "Scanning incentive distribution nodes...",
    "Finding details about offers from knowledge base...",
    "Compiling dealer-specific offer set...",
    "Matching promotions with user eligibility matrix...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
  finance: [
    "Fetching real-time finance model parameters...",
    "Finding details about finance from knowledge base...",
    "Running amortization projections...",
    "Validating warranty coverage tables...",
    "Synthesizing insights...",
    "Generating final response...",
  ],
} as const satisfies Record<"generic" | ContextKey, readonly string[]>;

// Detect contexts (prioritized and limited)
function detectContexts(
  query: string | undefined,
  maxContexts: number = 2
): ContextKey[] {
  const lower = (query ?? "").toLowerCase();
  if (!lower) return [];

  const detected: Array<{ context: ContextKey; priority: number }> = [];

  for (const context of Object.keys(keywordMap) as ContextKey[]) {
    const data = keywordMap[context];
    if (data.keywords.some((k) => lower.includes(k))) {
      detected.push({ context, priority: data.priority });
    }
  }

  detected.sort((a, b) => b.priority - a.priority);

  const ordered: ContextKey[] = [];
  for (const d of detected)
    if (!ordered.includes(d.context)) ordered.push(d.context);

  return ordered.slice(0, maxContexts);
}

// Build message sequence by interleaving pools from the detected contexts
function buildMessageSequence(
  contexts: readonly ContextKey[] | undefined
): string[] {
  const contextList =
    contexts && contexts.length ? contexts : (["generic"] as const);
  const pools = contextList.map(
    (ctx) =>
      thinkingMessages[ctx as keyof typeof thinkingMessages] ??
      thinkingMessages.generic
  );
  const maxLen = Math.max(...pools.map((p) => p.length));

  const seq: string[] = [];
  for (let i = 0; i < maxLen; i++) {
    for (const pool of pools) if (pool[i]) seq.push(pool[i]);
  }
  return seq.length ? seq : [...thinkingMessages.generic];
}

// Hook to rotate messages
function useThinkingMessages(
  query: string | undefined,
  options: { interval?: number; maxContexts?: number } = {}
): { message: string; contexts: ContextKey[]; sequence: string[] } {
  const { interval = 1500, maxContexts = 2 } = options;
  const [current, setCurrent] = useState<string>("");

  const contexts = useMemo(
    () => detectContexts(query, maxContexts),
    [query, maxContexts]
  );
  const sequence = useMemo(() => buildMessageSequence(contexts), [contexts]);

  useEffect(() => {
    if (!sequence.length) {
      setCurrent("");
      return;
    }
    
    let i = 0;
    setCurrent(sequence[i]);
    
    const intervalId = setInterval(() => {
      i = (i + 1) % sequence.length;
      setCurrent(sequence[i]);
    }, interval);
    
    return () => clearInterval(intervalId);
  }, [sequence, interval]);

  return { message: current, contexts, sequence };
}

// Component
export type ThinkingAnimationProps = {
  query?: string;
  interval?: number;
  maxContexts?: number;
};

// Memoized animation variants for better performance
const containerVariants = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.9 }
};

const contentVariants = {
  initial: { y: 5, opacity: 0.8 },
  animate: { y: 0, opacity: 1 }
};

const dotVariants = {
  animate: {
    y: [0, -8, 0],
    opacity: [0.7, 1, 0.7]
  }
};

const messageVariants = {
  initial: { opacity: 0, y: 15, scale: 0.95 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -15, scale: 0.95 }
};

const gradientVariants = {
  animate: {
    opacity: [0.3, 0.7, 0.3]
  }
};

// Memoized dot component to prevent unnecessary re-renders
const BouncingDot = memo(({ delay }: { delay: number }) => (
  <motion.div
    className="w-2 h-2 bg-blue-500 rounded-full"
    variants={dotVariants}
    animate="animate"
    transition={{
      duration: 0.6,
      repeat: Infinity,
      ease: "easeInOut",
      delay
    }}
  />
));

function ThinkingAnimation({
  query,
  interval = 2000,
  maxContexts = 2,
}: ThinkingAnimationProps) {
  const { message } = useThinkingMessages(query, { interval, maxContexts });

  return (
    <motion.div
      className="w-full relative p-2"
      variants={containerVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* Gradient Border Effect */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-blue-500/20 rounded-2xl"
        variants={gradientVariants}
        animate="animate"
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Main Content */}
      <motion.div
        className="relative bg-white dark:bg-primary rounded-xl py-3 px-4 shadow-lg border border-gray-200 dark:border-secondary"
        variants={contentVariants}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <div className="flex items-center space-x-2">
          <div className="flex space-x-1">
            <BouncingDot delay={0} />
            <BouncingDot delay={0.1} />
            <BouncingDot delay={0.2} />
          </div>
          <motion.span
            className="text-sm font-medium text-gray-700 dark:text-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            Thinking...
          </motion.span>
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={message}
            variants={messageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{
              duration: 0.4,
              ease: "easeInOut",
            }}
            className="mt-2"
          >
            <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
              {message}
            </p>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}

export default memo(ThinkingAnimation);
